import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
import http from "@/helper/http";
import { RESOURCE_CACHE, queryKeys } from "@/config/queryConfig";

// ==================== PRODUCT MAPPINGS ====================

export function useProductMappings(storeId, filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.mappings(storeId, filters),
    queryFn: async () => {
      const params = {
        page: options.page || 1,
        per_page: options.perPage || 20,
        ...filters,
      };
      const response = await http.get(`/v2/store/${storeId}/product-mappings`, {
        params,
      });
      return response.data;
    },
    enabled: computed(() => !!storeId),
    ...RESOURCE_CACHE.productMappings,
    ...options,
  });
}

export function useMappingDetails(storeId, mappingId, options = {}) {
  return useQuery({
    queryKey: queryKeys.mapping(storeId, mappingId),
    queryFn: async () => {
      const response = await http.get(
        `/v2/store/${storeId}/product-mappings/${mappingId}`
      );
      return response.data;
    },
    enabled: computed(() => !!storeId && !!mappingId),
    ...RESOURCE_CACHE.productMappings,
    ...options,
  });
}

export function useMappingSuggestions(storeId, otherStoreId, options = {}) {
  return useQuery({
    queryKey: queryKeys.mappingSuggestions(storeId, otherStoreId),
    queryFn: async () => {
      const response = await http.get(
        `/v2/store/${storeId}/product-mappings/suggestions`,
        {
          params: { store_id: otherStoreId, limit: options.limit || 25 },
        }
      );
      return response.data?.suggestions || [];
    },
    enabled: computed(() => !!storeId && !!otherStoreId),
    ...RESOURCE_CACHE.mappingSuggestions,
    ...options,
  });
}

// ==================== MUTATIONS ====================

export function useCreateMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ storeId, payload }) => {
      const response = await http.post(
        `/v2/store/${storeId}/product-mappings`,
        payload
      );
      return response.data;
    },
    onSuccess: (data, { storeId }) => {
      // Invalidate mappings list
      queryClient.invalidateQueries({ queryKey: ["productMappings", storeId] });
      // Invalidate suggestions if applicable
      queryClient.invalidateQueries({ queryKey: ["mappingSuggestions"] });
    },
  });
}

export function useUpdateMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ storeId, mappingId, payload }) => {
      const response = await http.patch(
        `/v2/store/${storeId}/product-mappings/${mappingId}`,
        payload
      );
      return response.data;
    },
    onSuccess: (data, { storeId, mappingId }) => {
      queryClient.invalidateQueries({ queryKey: ["productMappings", storeId] });
      queryClient.invalidateQueries({
        queryKey: ["productMapping", storeId, mappingId],
      });
    },
  });
}

export function useDeleteMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ storeId, mappingId }) => {
      await http.delete(`/v2/store/${storeId}/product-mappings/${mappingId}`);
      return { mappingId };
    },
    onSuccess: (data, { storeId }) => {
      queryClient.invalidateQueries({ queryKey: ["productMappings", storeId] });
    },
  });
}

// ==================== HELPERS ====================

export function useInvalidateMappings() {
  const queryClient = useQueryClient();

  return {
    invalidateMappings: (storeId) =>
      queryClient.invalidateQueries({ queryKey: ["productMappings", storeId] }),
    invalidateSuggestions: (storeId) =>
      queryClient.invalidateQueries({
        queryKey: ["mappingSuggestions", storeId],
      }),
    invalidateAll: () =>
      queryClient.invalidateQueries({ queryKey: ["productMappings"] }),
  };
}
